/**
 * StorePurchaseFlow - Data-driven flow controller for store purchase flows
 *
 * This controller manages the navigation through different steps of the purchase process.
 * It supports:
 * - Step definitions with conditions for skipping
 * - Back/Next navigation with proper step tracking
 * - Component rendering for each step
 * - Flow visualization
 */

class StorePurchaseFlow {
  constructor(flowDefinition) {
    this.flowDefinition = flowDefinition
    this.currentStepIndex = 0
    this.stepHistory = []
    this.flowState = {}
    this.visitedSteps = new Set() // Track which steps were actually visited in this session
  }

  /**
   * Get the current step definition
   */
  getCurrentStep() {
    return this.flowDefinition.steps[this.currentStepIndex]
  }

  /**
   * Get step by ID
   */
  getStepById(stepId) {
    return this.flowDefinition.steps.find(step => step.id === stepId)
  }

  /**
   * Check if a step should be skipped based on its condition
   */
  shouldSkipStep(step, context) {
    if (!step.skipCondition) return false

    if (typeof step.skipCondition === 'function') {
      return step.skipCondition(context)
    }

    return false
  }

  /**
   * Get the next visible step (skipping steps that should be skipped)
   */
  getNextVisibleStepIndex(fromIndex, context, direction = 'forward') {
    const increment = direction === 'forward' ? 1 : -1
    let nextIndex = fromIndex + increment

    while (nextIndex >= 0 && nextIndex < this.flowDefinition.steps.length) {
      const step = this.flowDefinition.steps[nextIndex]
      const shouldSkip = this.shouldSkipStep(step, context)

      if (!shouldSkip) {
        return nextIndex
      }

      nextIndex += increment
    }

    return null
  }

  /**
   * Navigate to the next step
   */
  goToNext(context) {
    const currentStep = this.flowDefinition.steps[this.currentStepIndex]

    // Mark current step as visited before moving
    if (currentStep) {
      this.visitedSteps.add(currentStep.id)
    }

    const nextIndex = this.getNextVisibleStepIndex(this.currentStepIndex, context, 'forward')

    if (nextIndex !== null) {
      this.stepHistory.push(this.currentStepIndex)
      this.currentStepIndex = nextIndex
      const nextStep = this.getCurrentStep()
      // Mark the new step as visited too
      if (nextStep) {
        this.visitedSteps.add(nextStep.id)
      }
      return nextStep
    }

    return null
  }

  /**
   * Navigate to the previous step
   */
  goBack(context) {
    // Use history to go back to the actual previous step
    if (this.stepHistory.length > 0) {
      this.currentStepIndex = this.stepHistory.pop()
      return this.getCurrentStep()
    }

    // Fallback to finding previous visible step
    const prevIndex = this.getNextVisibleStepIndex(this.currentStepIndex, context, 'backward')

    if (prevIndex !== null) {
      this.currentStepIndex = prevIndex
      return this.getCurrentStep()
    }

    return null
  }

  /**
   * Jump to a specific step by ID
   */
  goToStep(stepId, context) {
    const stepIndex = this.flowDefinition.steps.findIndex(step => step.id === stepId)

    if (stepIndex !== -1) {
      const step = this.flowDefinition.steps[stepIndex]

      if (!this.shouldSkipStep(step, context)) {
        this.stepHistory.push(this.currentStepIndex)
        this.currentStepIndex = stepIndex
        return step
      }
    }

    return null
  }

  /**
   * Reset the flow to the beginning
   */
  reset() {
    this.currentStepIndex = -1 // Start before first step
    this.stepHistory = []
    this.flowState = {}
    this.visitedSteps = new Set() // Clear visited steps on reset
  }

  /**
   * Initialize flow and get the first visible step
   */
  initialize(context) {
    this.reset()

    // Pre-populate visitedSteps with all visible steps for progress display
    // This ensures the progress header shows all steps that will be shown in this flow
    this.flowDefinition.steps.forEach(step => {
      if (!this.shouldSkipStep(step, context)) {
        this.visitedSteps.add(step.id)
      }
    })

    // Use goToNext to find first visible step
    return this.goToNext(context)
  }

  /**
   * Check if we're at the last step
   */
  isLastStep(context) {
    const nextIndex = this.getNextVisibleStepIndex(this.currentStepIndex, context, 'forward')
    return nextIndex === null
  }

  /**
   * Check if we're at the first step
   */
  isFirstStep() {
    return this.currentStepIndex === 0 && this.stepHistory.length === 0
  }

  /**
   * Get all visible steps for progress visualization
   */
  getVisibleSteps(context) {
    const result = this.flowDefinition.steps.filter(step => {
      const shouldSkip = !this.shouldSkipStep(step, context)
      return shouldSkip
    })

    return result
  }

  /**
   * Get all steps for progress header (ignoring skip conditions)
   * This shows the complete user journey in the progress bar
   */
  getAllSteps() {
    return this.flowDefinition.steps
  }

  /**
   * Get steps that were actually visited in this session (for progress header)
   * This only shows steps the user actually went through, not auto-skipped ones
   * Steps with alwaysShow: true are always included regardless of visited status
   */
  getVisitedSteps() {
    return this.flowDefinition.steps.filter(step =>
      this.visitedSteps.has(step.id) || step.alwaysShow === true
    )
  }

  /**
   * Get current step progress (for progress bars, etc.)
   */
  getProgress(context) {
    const visibleSteps = this.getVisibleSteps(context)
    const currentVisibleIndex = visibleSteps.findIndex(
      step => step.id === this.getCurrentStep().id
    )

    return {
      current: currentVisibleIndex + 1,
      total: visibleSteps.length,
      percentage: ((currentVisibleIndex + 1) / visibleSteps.length) * 100
    }
  }

  /**
   * Update flow state
   */
  updateState(key, value) {
    this.flowState[key] = value
  }

  /**
   * Get flow state
   */
  getState(key) {
    return this.flowState[key]
  }
}

// Flow registry to store all loaded flows
const flowRegistry = {}

/**
 * Register a flow definition
 */
export const registerFlow = (flowId, flowDefinition) => {
  flowRegistry[flowId] = flowDefinition
}

/**
 * Get a flow definition by ID
 */
export const getFlow = (flowId) => {
  return flowRegistry[flowId]
}

/**
 * Create a new flow instance
 */
export const createFlowInstance = (flowId) => {
  const flowDefinition = getFlow(flowId)

  if (!flowDefinition) {
    console.warn(`Flow "${flowId}" not found, using default flow`)
    return new StorePurchaseFlow(getFlow('default') || { steps: [] })
  }

  return new StorePurchaseFlow(flowDefinition)
}

/**
 * Load all flow definitions from the project and aldoo-ra
 */
export const loadFlows = async () => {
  try {
    // Load default flow from aldoo-ra
    const defaultFlow = await import('./flows/purchase-flow.jsx')
    registerFlow('default', defaultFlow.default)
  } catch (error) {
    console.warn('Default purchase flow not found:', error)
  }

  // Load project-specific flows
  try {
    const projectFlows = import.meta.glob('/components/Store/flows/*.jsx', { eager: true })

    Object.entries(projectFlows).forEach(([path, module]) => {
      const flowId = path.match(/flows\/(.+)\.jsx$/)?.[1]
      if (flowId && module.default) {
        registerFlow(flowId, module.default)
      }
    })
  } catch (error) {
    console.warn('Error loading project flows:', error)
  }
}

export default StorePurchaseFlow
